import { CreateCampaignFormValue } from '@features/Admin/Campaign/model/type';
import { Box, styled, TextField, useTheme } from '@mui/material';
import { IconMinus, IconPlus } from '@shared/assets/images/svgs';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { RootState, useAppSelector } from '@shared/store';
import { toNumber } from 'lodash';
import React from 'react';
import { Control } from 'react-hook-form';
import { ChangeRetryValueByButtonData } from '../campaignRetryFields/CampaignRetryFields';

interface CounterInputProps {
  id: string;
  retryFieldValue: string;
  fieldName: string;
  isErrored: boolean;
  control: Control<CreateCampaignFormValue, any>;
  handleOnChangeRetryFieldInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOnChangeRetryValueByButton: (data: ChangeRetryValueByButtonData) => void;
  min?: number;
  max?: number;
}

const BoxStyled = styled(Box)(({ theme }) => ({
  color: 'inherit',
  display: 'grid',
  gridTemplateColumns: '52px 1fr 52px',
  alignItems: 'center',
  width: '100%',
  border: `1px solid ${theme.palette.border?.default ?? theme.palette.divider}`,
  borderRadius: '10px',
}));

const WrappedIcon = styled(Box)(({ theme }) => ({
  backgroundColor: 'transparent',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '52px',
  minWidth: '52px',
  height: '100%',
  padding: 0,
  border: 0,

  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    transition: '0.1s ease-in',
  },
}));

const TextFieldStyled = styled(TextField)(() => ({
  width: '100%',
  minWidth: 0,
  border: 0,
  '& .MuiInputBase-root': {
    width: '100%',
    minWidth: 0,
  },
  '& .MuiInputBase-input': {
    textAlign: 'center',
    paddingLeft: 0,
    paddingRight: 0,
    MozAppearance: 'textfield',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  },
  fieldset: {
    border: 0,
  },
}));

export const CounterInput: React.FC<CounterInputProps> = ({
  id,
  retryFieldValue,
  fieldName,
  isErrored,
  handleOnChangeRetryFieldInput,
  handleOnChangeRetryValueByButton,
  min,
  max,
}) => {
  const theme = useTheme();
  const currentUser = useAppSelector((state: RootState) => state.auth.authenticatedUser);
  const isViewer = currentUser?.role === UserRolesEnum.VIEWER;

  const viewerStyles = isViewer
    ? {
        cursor: 'default',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      }
    : {};

  return (
    <BoxStyled
      sx={{
        ...(isErrored
          ? {
              borderColor: `${theme.palette.error.main} !important`,
            }
          : {}),
      }}>
      <WrappedIcon
        sx={{
          borderRadius: '10px 0 0 10px',
          ...viewerStyles,
        }}
        onClick={() => {
          if (!isViewer) {
            handleOnChangeRetryValueByButton({
              value: toNumber(retryFieldValue) - 1,
              name: fieldName,
            });
          }
        }}>
        <IconMinus />
      </WrappedIcon>
      <TextFieldStyled
        id={id}
        fullWidth
        size="small"
        onChange={handleOnChangeRetryFieldInput}
        name={fieldName}
        disabled={isViewer}
        type="number"
        value={retryFieldValue}
      />
      <WrappedIcon
        sx={{
          borderRadius: '0 10px 10px 0',
          ...viewerStyles,
        }}
        onClick={() => {
          if (!isViewer) {
            let next = toNumber(retryFieldValue) + 1;
            if (max !== undefined) {
              next = Math.min(max, next);
            }
            handleOnChangeRetryValueByButton({
              value: next,
              name: fieldName,
            });
          }
        }}>
        <IconPlus />
      </WrappedIcon>
    </BoxStyled>
  );
};
